import Button, { type ButtonProps } from '@mui/material/Button';
import ButtonGroup, { type ButtonGroupProps } from '@mui/material/ButtonGroup';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import { type ForwardedRef, forwardRef, useState } from 'react';

import type { SelectionProps } from '../Selection';
import type { OptionSelectionProps } from '../types';

type ButtonGroupSelectionProps = Pick<
  SelectionProps,
  'value' | 'defaultValue' | 'sx' | 'error' | 'disabled' | 'required' | 'helperText'
> & {
  options: OptionSelectionProps[];
  label?: string;
  buttonProps?: Omit<ButtonProps, 'variant'>;
  buttonGroupProps?: Omit<ButtonGroupProps, 'variant'>;
  onChange?: (value: OptionSelectionProps['value']) => void;
  onBlur?: () => void;
};

const ButtonGroupSelection = forwardRef(
  (props: ButtonGroupSelectionProps, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      value,
      defaultValue,
      options,
      onChange,
      onBlur,
      label,
      helperText,
      buttonProps,
      buttonGroupProps,
      ...restProps
    } = props;

    const [selectedValue, setSelectedValue] = useState(value ?? defaultValue);

    const handleChange = (value: OptionSelectionProps['value']) => {
      setSelectedValue(value);
      onChange?.(value);
      onBlur?.();
    };

    return (
      <FormControl {...restProps} ref={ref}>
        {label ? <InputLabel sx={{ position: 'relative', mb: 1 }}>{label}</InputLabel> : null}
        <ButtonGroup variant='outlined' aria-label='outlined button group' {...buttonGroupProps}>
          {options.map(({ value, label }, index) => {
            const isSelected = selectedValue === value;
            return (
              <Button
                key={`${index}-${value}`}
                variant={isSelected ? 'contained' : 'outlined'}
                onClick={() => handleChange(value)}
                size='small'
                sx={{
                  px: 1,
                  fontWeight: 400,
                  color: 'text.primary',
                  backgroundColor: isSelected ? 'primary.lighter' : undefined,
                  border: '2px solid',
                  borderColor: 'grey.400',
                  ':hover': {
                    backgroundColor: isSelected ? 'primary.lighter' : 'grey.100',
                    borderColor: 'grey.400',
                  },
                }}
                {...buttonProps}
              >
                {label}
              </Button>
            );
          })}
        </ButtonGroup>
        {helperText ? <FormHelperText sx={{ mt: 1 }}>{helperText}</FormHelperText> : null}
      </FormControl>
    );
  },
);

ButtonGroupSelection.displayName = 'ButtonGroupSelection';

export { ButtonGroupSelection };
export type { ButtonGroupSelectionProps };
